import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlusCircle, Pencil, Trash2, X, ChevronDown } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import {
  listObjectHistory,
  type ObjectHistoryRow,
  type ObjectHistoryListFilters,
} from '@core/supabase/services/objectHistoryService';
import { orgService } from '@core/supabase/services/orgService';
import type { OrgUser } from '@core/types';

const ENTITY_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  applications: 'Applications',
  candidates: 'Candidates',
  requisitions: 'Requisitions',
  clients: 'Clients',
  jobs: 'Jobs',
  interview_prep: 'Interview prep',
  reports: 'Reports',
  interviews_hub: 'Interview hub',
  activities_assigned: 'Activities',
  email_threads: 'Emails',
  role_fit_matrix: 'Role fit matrix',
  rolefit_ai_projections: 'RoleFit AI',
  team: 'Team',
  scheduler: 'Scheduler',
  calendar_notes: 'Calendar & notes',
  all: 'All tracked objects',
};

/** entity_type values that receive rows from ggj_b2b_object_history triggers */
const ENTITY_TYPES_WITH_DB_HISTORY = new Set([
  'candidates',
  'clients',
  'jobs',
  'applications',
  'requisitions',
  'team',
  'all',
]);

function labelForEntity(entityKey: string): string {
  if (ENTITY_LABEL[entityKey]) return ENTITY_LABEL[entityKey];
  return entityKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function actorLabel(row: ObjectHistoryRow): string {
  const u = row.changed_by;
  if (u && typeof u === 'object' && !Array.isArray(u)) {
    return (u.display_name || u.email || '—').trim() || '—';
  }
  if (Array.isArray(u) && u[0] && typeof u[0] === 'object') {
    const x = u[0] as { display_name?: string | null; email?: string | null };
    return (x.display_name || x.email || '—').trim() || '—';
  }
  return '—';
}

function shortId(id: string): string {
  if (!id || id.length < 10) return id;
  return `${id.slice(0, 8)}…`;
}

function ActionIcon({ action }: { action: string }) {
  const a = (action ?? '').toLowerCase();
  if (a === 'insert') return <PlusCircle className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />;
  if (a === 'delete') return <Trash2 className="w-4 h-4 text-red-400/90 shrink-0" aria-hidden />;
  return <Pencil className="w-4 h-4 text-amber-400/90 shrink-0" aria-hidden />;
}

/** §25.B — Dot color by event type for the timeline view. */
function timelineDotClass(action: string): string {
  const a = (action ?? '').toLowerCase();
  if (a === 'insert') return 'bg-emerald-500 ring-emerald-500/25';     // create / approve
  if (a === 'delete') return 'bg-red-500 ring-red-500/25';              // delete / reject
  if (a === 'update') return 'bg-amber-500 ring-amber-500/25';          // status change
  return 'bg-zinc-400 ring-zinc-400/25';                                 // read / other
}

function verbForAction(action: string): string {
  const a = (action ?? '').toLowerCase();
  if (a === 'insert') return 'created';
  if (a === 'delete') return 'deleted';
  if (a === 'update') return 'updated';
  return action;
}

function TableSkeleton({ cols = 8 }: { cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: 8 }).map((_, i) => (
        <tr key={i} className="border-b vscode-border">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-2">
              <div className="h-4 rounded animate-pulse bg-[var(--vscode-bg-tertiary)] opacity-70" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/**
 * Server-backed object history (ggj_b2b_object_history). Pagination at bottom only.
 * Phase 7 §2.10 — filters + row diff viewer.
 */
export function ObjectHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const entityKey = (searchParams.get('entity') ?? 'all').trim() || 'all';
  const dateFromParam = searchParams.get('from') ?? '';
  const dateToParam = searchParams.get('to') ?? '';
  const actorParam = searchParams.get('actor') ?? '';
  const actionParam = (searchParams.get('action') ?? '') as '' | 'insert' | 'update' | 'delete';

  const { user } = useAuth();
  const orgId = user?.orgId ?? null;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState<ObjectHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [team, setTeam] = useState<OrgUser[]>([]);
  const [selected, setSelected] = useState<ObjectHistoryRow | null>(null);

  const entityLabel = labelForEntity(entityKey);
  const hasDbScope = ENTITY_TYPES_WITH_DB_HISTORY.has(entityKey);

  const filters: ObjectHistoryListFilters = useMemo(
    () => ({
      dateFrom: dateFromParam || undefined,
      dateTo: dateToParam || undefined,
      changedByOrgUserId: actorParam || undefined,
      action: actionParam || undefined,
    }),
    [dateFromParam, dateToParam, actorParam, actionParam]
  );

  useEffect(() => {
    if (!orgId) return;
    orgService.getTeamMembers(orgId).then(setTeam).catch(() => setTeam([]));
  }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId) {
      setRows([]);
      setTotal(0);
      return;
    }
    if (!hasDbScope) {
      setRows([]);
      setTotal(0);
      setLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { rows: data, total: t } = await listObjectHistory(
        orgId,
        entityKey,
        page,
        pageSize,
        filters
      );
      setRows(data);
      setTotal(t);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setLoadError(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [orgId, entityKey, page, pageSize, hasDbScope, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [orgId, entityKey, filters]);

  useEffect(() => {
    document.title = `Object history · ${entityLabel} - GoGrabJob`;
  }, [entityLabel]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / pageSize) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [total, pageSize, page]);

  const setFilter = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4 sm:p-6">
      <PageHeader
        variant="minimal"
        className="!mb-4 shrink-0"
        breadcrumb={[{ label: 'Object history' }, { label: entityLabel }]}
      />

      {/* ── Filter chip row ── */}
      {hasDbScope ? (
        <div className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border px-3 py-2.5 vscode-border vscode-bg-secondary">
          <select
            className="input text-xs py-1.5 min-w-[140px]"
            value={entityKey}
            onChange={(e) => setFilter({ entity: e.target.value || 'all' })}
            aria-label="Object type filter"
          >
            <option value="all">All tracked</option>
            <option value="candidates">Candidates</option>
            <option value="clients">Clients</option>
            <option value="jobs">Jobs</option>
            <option value="applications">Applications</option>
            <option value="requisitions">Requisitions</option>
            <option value="team">Team</option>
          </select>
          <input
            type="date"
            className="input text-xs py-1.5 w-32"
            value={dateFromParam}
            onChange={(e) => setFilter({ from: e.target.value })}
            aria-label="From date"
          />
          <input
            type="date"
            className="input text-xs py-1.5 w-32"
            value={dateToParam}
            onChange={(e) => setFilter({ to: e.target.value })}
            aria-label="To date"
          />
          <select
            className="input text-xs py-1.5 min-w-[160px]"
            value={actorParam}
            onChange={(e) => setFilter({ actor: e.target.value })}
            aria-label="Actor filter"
          >
            <option value="">Anyone</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>
                {(m.display_name ?? m.email).trim()}
              </option>
            ))}
          </select>
          <select
            className="input text-xs py-1.5 min-w-[110px]"
            value={actionParam}
            onChange={(e) => setFilter({ action: e.target.value })}
            aria-label="Action filter"
          >
            <option value="">Any action</option>
            <option value="insert">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
          <button
            type="button"
            className="text-xs vscode-text-tertiary hover:vscode-text-primary"
            onClick={() => setFilter({ from: null, to: null, actor: null, action: null })}
          >
            Clear
          </button>

          {/* Legend dots */}
          <div className="ml-auto flex flex-wrap items-center gap-3">
            {[
              { dot: 'bg-emerald-500', label: 'Create' },
              { dot: 'bg-amber-500', label: 'Update' },
              { dot: 'bg-red-500', label: 'Delete' },
              { dot: 'bg-zinc-400', label: 'Other' },
            ].map(({ dot, label }) => (
              <span key={label} className="flex items-center gap-1 text-[11px] vscode-text-tertiary">
                <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden />
                {label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* ── Main body: timeline + diff panel ── */}
      <div className="flex min-h-0 flex-1 gap-5 overflow-hidden">
        {/* Timeline */}
        <div className="min-w-0 flex-1 overflow-y-auto">
          {!orgId ? (
            <p className="py-10 text-center text-sm vscode-text-secondary">
              Sign in with an organisation to view object history.
            </p>
          ) : !hasDbScope ? (
            <p className="py-10 text-sm vscode-text-secondary">
              Select <strong className="vscode-text-primary">All tracked</strong> or an entity with
              database history (candidates, clients, jobs, applications, requisitions, team).
            </p>
          ) : loadError ? (
            <p className="py-10 text-sm vscode-text-secondary leading-relaxed">
              Could not load history
              {loadError.includes('relation') || loadError.includes('does not exist') ? (
                <>
                  . Apply migration{' '}
                  <code className="text-xs">068_ggj_b2b_object_history.sql</code> to your Supabase
                  project, then refresh.
                </>
              ) : (
                <> — {loadError}</>
              )}
            </p>
          ) : loading ? (
            /* Timeline skeletons */
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-0 bottom-0 w-0.5 vscode-bg-tertiary" aria-hidden />
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="relative mb-4 pl-5">
                  <div className="absolute -left-[1px] top-1 h-3 w-3 rounded-full animate-pulse vscode-bg-tertiary ring-2 ring-[var(--vscode-bg-secondary)]" />
                  <div className="rounded-[10px] border p-3 vscode-border vscode-bg-secondary space-y-2">
                    <div className="h-3 w-48 animate-pulse rounded vscode-bg-tertiary" />
                    <div className="h-2.5 w-32 animate-pulse rounded vscode-bg-tertiary" />
                  </div>
                </div>
              ))}
            </div>
          ) : total === 0 ? (
            <p className="py-10 text-sm vscode-text-secondary">
              No rows for these filters. Try widening the date range or clearing filters.
            </p>
          ) : (
            /* §25.B — timeline */
            <div className="relative pl-6">
              {/* Vertical line */}
              <div
                className="absolute left-[7px] top-2 w-0.5 vscode-bg-tertiary"
                style={{ bottom: 16 }}
                aria-hidden
              />

              {rows.map((row) => {
                const actor = actorLabel(row);
                const verb = verbForAction(row.action);
                const obj = row.table_name?.replace(/_/g, ' ') ?? 'record';
                const hasDiff = row.attribute_name && (row.old_value || row.new_value);
                const isSelected = selected?.id === row.id;

                return (
                  <div key={row.id} className="relative mb-3 pl-5">
                    {/* Dot */}
                    <div
                      className={`absolute -left-[1px] top-1.5 h-3 w-3 shrink-0 rounded-full ring-[3px] ring-[var(--vscode-bg-primary)] ${timelineDotClass(row.action)}`}
                      aria-hidden
                    />

                    {/* Card */}
                    <button
                      type="button"
                      onClick={() => setSelected(isSelected ? null : row)}
                      className={[
                        'w-full rounded-[10px] border px-3 py-2.5 text-left transition-colors',
                        isSelected
                          ? 'border-[#10B981]/60 bg-emerald-500/5'
                          : 'vscode-border vscode-bg-secondary hover:vscode-bg-hover',
                      ].join(' ')}
                    >
                      <div className="flex items-start gap-2">
                        <TableAvatar name={actor} size={20} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] vscode-text-primary">
                            <span className="font-medium">{actor}</span>
                            {' '}<span className="vscode-text-secondary">{verb}</span>
                            {' '}<span className="font-mono text-[12px] vscode-text-secondary">{obj}</span>
                          </p>
                          {hasDiff && (
                            <p className="mt-0.5 font-mono text-[11px] vscode-text-tertiary">
                              {row.attribute_name}:{' '}
                              <span className="text-red-400 line-through">{row.old_value ?? '—'}</span>
                              {' → '}
                              <span className="text-emerald-400">{row.new_value ?? '—'}</span>
                            </p>
                          )}
                        </div>
                        <time className="shrink-0 text-[11px] vscode-text-tertiary">
                          {formatDateTimeGmt(row.created_at)}
                        </time>
                      </div>
                    </button>
                  </div>
                );
              })}

              {/* Load earlier */}
              {total > page * pageSize && (
                <div className="relative pl-5 pb-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    className="flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[13px] vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden />
                    Load earlier
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Diff detail panel */}
        {selected && hasDbScope ? (
          <aside className="w-[min(360px,100%)] shrink-0 overflow-y-auto rounded-xl border vscode-border vscode-bg-secondary">
            <div className="flex items-center justify-between gap-2 border-b px-3 py-2 vscode-border">
              <p className="text-[13px] font-semibold vscode-text-primary">Change detail</p>
              <button
                type="button"
                className="rounded p-1 transition-colors hover:vscode-bg-hover"
                aria-label="Close diff"
                onClick={() => setSelected(null)}
              >
                <X className="h-4 w-4 vscode-text-tertiary" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
              <div>
                <p className="text-[10px] uppercase vscode-text-tertiary mb-0.5">Attribute</p>
                <p className="font-mono text-[11px] vscode-text-primary">{selected.attribute_name}</p>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded-lg border p-2 vscode-border vscode-bg-tertiary">
                  <p className="text-[10px] uppercase vscode-text-tertiary mb-1">Before</p>
                  <pre className="whitespace-pre-wrap break-words vscode-text-secondary">{selected.old_value ?? '—'}</pre>
                </div>
                <div className="rounded-lg border p-2 bg-emerald-500/5 border-emerald-500/20">
                  <p className="text-[10px] uppercase vscode-text-tertiary mb-1">After</p>
                  <pre className="whitespace-pre-wrap break-words vscode-text-primary">{selected.new_value ?? '—'}</pre>
                </div>
              </div>
              <p className="text-[10px] vscode-text-tertiary">
                {formatDateTimeGmt(selected.created_at)} · {actorLabel(selected)} · {selected.action}
              </p>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
